'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  formatUsdFromCents,
  listTomVendingDailySummaryDays,
  queryTomVendingDailySummary,
  type TomVendingDailySummaryCashRow,
  type TomVendingDailySummaryFillRow,
  type TomVendingDailySummaryHeader,
  type TomVendingDailySummaryProductUsedRow,
} from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';
import { downloadDailySummaryXlsx } from '@/utils/tomsVendingReportExport';

const EMPTY_SUMMARY: TomVendingDailySummaryHeader = {
  visitCount: 0,
  locationCount: 0,
  totalCashCents: 0,
  totalFillQty: 0,
  totalRejectQty: 0,
  totalRecycleQty: 0,
  totalUnitsSold: 0,
  totalRetailCents: 0,
};

type LocRow = { id: number; name?: string; isWarehouse?: boolean };

type ReportTab = 'money' | 'product-used' | 'summary';

function sumCashCents(
  table: { getFilteredRowModel: () => { rows: Array<{ original: TomVendingDailySummaryCashRow }> } },
  key: 'billsCents' | 'coinsCents' | 'totalCents',
): number {
  return table.getFilteredRowModel().rows.reduce((s, r) => s + (r.original[key] ?? 0), 0);
}

function MoneyTotalFooter({ value }: { value: string }) {
  return (
    <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  );
}

const TomsVendingDailySummaryReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();

  const [date, setDate] = useState('');
  const [days, setDays] = useState<string[]>([]);
  const [daysLoading, setDaysLoading] = useState(false);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [tab, setTab] = useState<ReportTab>('summary');
  const [cashRows, setCashRows] = useState<TomVendingDailySummaryCashRow[]>([]);
  const [productUsed, setProductUsed] = useState<TomVendingDailySummaryProductUsedRow[]>([]);
  const [fills, setFills] = useState<TomVendingDailySummaryFillRow[]>([]);
  const [summary, setSummary] = useState<TomVendingDailySummaryHeader>(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(false);

  const locationTomVendingId = (() => {
    if (locationId === '' || locationId == null) {
      return undefined;
    }
    const n = Number(locationId);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();

  useEffect(() => {
    if (locationTomVendingId == null) {
      setDays([]);
      setDate('');
      setDaysLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setDaysLoading(true);
      try {
        const r = await listTomVendingDailySummaryDays({ locationTomVendingId });
        if (cancelled) return;
        setDays(r.days);
        setDate((prev) => (prev && r.days.includes(prev) ? prev : r.days[0] ?? ''));
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load days with data');
          setDays([]);
          setDate('');
        }
      } finally {
        if (!cancelled) setDaysLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationTomVendingId, showError]);

  const fetchData = useCallback(async () => {
    if (!date || locationTomVendingId == null) {
      setCashRows([]);
      setProductUsed([]);
      setFills([]);
      setSummary(EMPTY_SUMMARY);
      return;
    }
    setIsLoading(true);
    try {
      const r = await queryTomVendingDailySummary({
        date,
        locationTomVendingId,
      });
      setCashRows(r.cashRows);
      setProductUsed(r.productUsed);
      setFills(r.fills);
      setSummary(r.summary);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load daily summary');
      setCashRows([]);
      setProductUsed([]);
      setFills([]);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setIsLoading(false);
    }
  }, [date, locationTomVendingId, showError]);

  useEffect(() => {
    if (locationTomVendingId == null || !date) {
      setCashRows([]);
      setProductUsed([]);
      setFills([]);
      setSummary(EMPTY_SUMMARY);
      return;
    }
    void fetchData();
  }, [fetchData, date, locationTomVendingId]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const moneyColumns = useMemo<MRT_ColumnDef<TomVendingDailySummaryCashRow>[]>(
    () => [
      {
        accessorKey: 'visitDate',
        header: 'Visit date',
        size: 140,
        Cell: ({ cell }) => formatVisitDateLabel(String(cell.getValue() ?? '')),
        Footer: () => <MoneyTotalFooter value="Total" />,
      },
      {
        accessorKey: 'locationName',
        header: 'Location',
        Cell: ({ row }) => row.original.locationName?.trim() || `Location ${row.original.locationTomVendingId}`,
      },
      {
        id: 'machine',
        header: 'Machine',
        Cell: ({ row }) =>
          row.original.vendingMachineId == null
            ? 'Site total'
            : row.original.assetId?.trim() || `#${row.original.vendingMachineId}`,
      },
      {
        accessorKey: 'billsCents',
        header: 'Bills',
        size: 100,
        Cell: ({ row }) => formatUsdFromCents(row.original.billsCents),
        Footer: ({ table }) => <MoneyTotalFooter value={formatUsdFromCents(sumCashCents(table, 'billsCents'))} />,
      },
      {
        accessorKey: 'coinsCents',
        header: 'Coins',
        size: 100,
        Cell: ({ row }) => formatUsdFromCents(row.original.coinsCents),
        Footer: ({ table }) => <MoneyTotalFooter value={formatUsdFromCents(sumCashCents(table, 'coinsCents'))} />,
      },
      {
        accessorKey: 'totalCents',
        header: 'Total',
        size: 110,
        Cell: ({ row }) => formatUsdFromCents(row.original.totalCents),
        Footer: ({ table }) => <MoneyTotalFooter value={formatUsdFromCents(sumCashCents(table, 'totalCents'))} />,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'cashSource',
        header: 'Status',
        size: 110,
        Cell: ({ row }) =>
          row.original.cashSource === 'pending' ? (
            <Chip label="Open visit" size="small" variant="outlined" />
          ) : (
            'Finalized'
          ),
      },
    ],
    [],
  );

  const usedColumns = useMemo<MRT_ColumnDef<TomVendingDailySummaryProductUsedRow>[]>(
    () => [
      { accessorKey: 'upc', header: 'UPC', size: 130 },
      {
        accessorKey: 'itemName',
        header: 'Product',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      { accessorKey: 'unitsSold', header: 'Units used', size: 110 },
      {
        accessorKey: 'retailCents',
        header: 'Retail',
        size: 110,
        Cell: ({ row }) => formatUsdFromCents(row.original.retailCents),
      },
    ],
    [],
  );

  const fillColumns = useMemo<MRT_ColumnDef<TomVendingDailySummaryFillRow>[]>(
    () => [
      { accessorKey: 'upc', header: 'UPC', size: 130 },
      {
        accessorKey: 'itemName',
        header: 'Product',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      { accessorKey: 'fillQty', header: 'Filled', size: 90 },
      { accessorKey: 'rejectQty', header: 'Rejected', size: 90 },
      { accessorKey: 'recycleQty', header: 'Recycled', size: 90 },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    { label: 'Daily summary' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Daily summary" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
          {selectedLoc && date
            ? `Whole-day view for ${formatVisitDateLabel(date)} at ${selectedLoc.name?.trim() || `Location ${selectedLoc.id}`}.`
            : 'Choose a location, then a visit day for that location.'}{' '}
          Money is cash collected per machine (or a site total when cash was not recorded per machine). Product
          used is inferred units sold. Fill amounts come from saved site-visit tallies.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-start' }}>
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 280, order: 0 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => setLocationId(v?.id ?? '')}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, l) => (
              <li {...props} key={l.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{l.name?.trim() || `Location ${l.id}`}</Typography>
                  {tomVendingLocationIsWarehouse(l) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" required placeholder="Select a location" helperText=" " />
            )}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 240, order: 1 }}
            options={days}
            loading={daysLoading}
            disabled={locationTomVendingId == null}
            disableClearable={Boolean(date) && days.length > 0}
            value={date || null}
            onChange={(_, v) => setDate(v ?? '')}
            getOptionLabel={(ymd) => formatVisitDateLabel(ymd)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Day"
                placeholder={locationTomVendingId == null ? 'Select a location first' : 'Select a day'}
                helperText={
                  locationTomVendingId == null
                    ? 'Select a location first'
                    : !daysLoading && days.length === 0
                      ? 'No visit days at this location'
                      : ' '
                }
              />
            )}
            noOptionsText="No days with site visit data at this location"
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => void fetchData()}
            disabled={isLoading || !date || locationTomVendingId == null}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              if (!date || !selectedLoc) return;
              downloadDailySummaryXlsx({
                date,
                locationName: selectedLoc.name?.trim() || `Location ${selectedLoc.id}`,
                summary,
                cashRows,
                productUsed,
                fills,
              });
            }}
            disabled={isLoading || !date || locationTomVendingId == null}
          >
            Download Excel
          </Button>
          <Button
            component={RouterLink}
            to="/app/toms-vending/reports"
            variant="text"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            All reports
          </Button>
        </Box>

        {locationTomVendingId == null ? (
          <Typography variant="body2" color="text.secondary">
            Select a location to load visit days.
          </Typography>
        ) : !daysLoading && days.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No site visit days at this location. Record money collected or save machine tallies to create a day.
          </Typography>
        ) : null}

        {date ? (
          <>
        <Tabs
          value={tab}
          onChange={(_, v: ReportTab) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="summary" label="Summary" />
          <Tab value="money" label="Money" />
          <Tab value="product-used" label="Product used" />
        </Tabs>

        {tab === 'summary' ? (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <SummaryStat label="Visits" value={String(summary.visitCount)} />
              <SummaryStat label="Locations" value={String(summary.locationCount)} />
              <SummaryStat label="Cash collected" value={formatUsdFromCents(summary.totalCashCents)} />
              <SummaryStat label="Units used" value={String(summary.totalUnitsSold)} />
              <SummaryStat label="Retail (used)" value={formatUsdFromCents(summary.totalRetailCents)} />
              <SummaryStat label="Units filled" value={String(summary.totalFillQty)} />
              {summary.totalRejectQty > 0 ? (
                <SummaryStat label="Rejected" value={String(summary.totalRejectQty)} />
              ) : null}
            </Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Products filled
            </Typography>
            <MaterialReactTable
              columns={fillColumns}
              data={fills}
              enableGlobalFilter
              state={{ isLoading }}
              initialState={{ density: 'compact' }}
            />
          </Box>
        ) : null}

        {tab === 'money' ? (
          <MaterialReactTable
            columns={moneyColumns}
            data={cashRows}
            enableGlobalFilter
            enableStickyFooter
            muiTableBodyRowProps={({ row }) => ({
              onClick: () =>
                navigate(
                  `/app/toms-vending/site-visits/${row.original.siteVisitId}?locationId=${row.original.locationTomVendingId}`,
                ),
              sx: { cursor: 'pointer' },
            })}
            muiTableFooterCellProps={{
              sx: { fontWeight: 700, bgcolor: 'action.hover' },
            }}
            state={{ isLoading }}
            initialState={{ density: 'compact' }}
          />
        ) : null}

        {tab === 'product-used' ? (
          <MaterialReactTable
            columns={usedColumns}
            data={productUsed}
            enableGlobalFilter
            state={{ isLoading }}
            initialState={{ density: 'compact' }}
          />
        ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
};

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 140,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

export default TomsVendingDailySummaryReportPage;
